import React, { useState, useEffect, useRef, useMemo } from "react";
import { EyeOutlined, QrcodeOutlined, LockOutlined } from "@ant-design/icons";
import { MapPin } from "lucide-react";
import { cdnUrl } from "../../../config/mediaPaths";
import { FOOTER } from "../../../config/zinihubConfig";
import BusinessMenu from "./BusinessMenu";
import { getCategoryIcon, getStoreCategoryLines } from "../categoryIcons";
import HangingClosedBoard from "../../../components/HangingClosedBoard";
import styles from "./BusinessCard.module.css";
import { getGuidedJourneyBusinessTarget } from "../../../config/guidedJourneyConfig";

function BusinessCardMedia({ shop, bizImgUrl, isDeleted, selectedHubDomain, onPreview, onQRCode }) {
  const [imgError, setImgError] = useState(false);
  const outerRef = useRef(null);
  const storefrontOpen = shop.bizOpenStatus !== false;

  useEffect(() => { setImgError(false); }, [bizImgUrl, shop.bizId]);

  const handleImagePreview = (e) => {
    e.stopPropagation();
    onPreview?.(shop);
  };

  const handleQRClick = (e) => {
    e.stopPropagation();
    onQRCode?.(shop);
  };

  const initials = useMemo(() => {
    const name = shop.name || "";
    return name.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "??";
  }, [shop.name]);

  const imageSlide = bizImgUrl && !imgError ? (
    <img
      src={bizImgUrl}
      alt=""
      className={styles.mediaImage}
      onClick={handleImagePreview}
      onError={() => setImgError(true)}
    />
  ) : (
    <div className={styles.mediaPlaceholder} onClick={handleImagePreview}>
      <span className={styles.placeholderInitials}>{initials}</span>
    </div>
  );

  if (isDeleted) {
    return (
      <div ref={outerRef} className={styles.mediaWrap}>
        <div className={styles.mediaInner}>{imageSlide}</div>
        <div className={styles.mediaOverlay}>
          <button className={styles.overlayBtn} onClick={handleImagePreview} aria-label="Preview business">
            <EyeOutlined />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={outerRef} className={styles.mediaWrap}>
      {!storefrontOpen ? <HangingClosedBoard label="Closed" compact /> : null}
      <div className={styles.mediaInner}>
        <div className={styles.mediaSlide}>{imageSlide}</div>
      </div>
      <div className={styles.mediaOverlay}>
        <button className={styles.overlayBtn} onClick={handleImagePreview} aria-label="Preview business">
          <EyeOutlined />
        </button>
        <button className={styles.overlayBtn} onClick={handleQRClick} aria-label="QR Code">
          <QrcodeOutlined />
        </button>
      </div>
    </div>
  );
}

const BusinessCard = React.memo(function BusinessCard({
  shop,
  selectedHubDomain,
  isDeleted,
  onManage,
  onEdit,
  onDelete,
  onRestore,
  onPreview,
  onQRCode,
}) {
  const basenameOnly = (k) => { const s = String(k || ""); const i = s.lastIndexOf("/"); return i >= 0 ? s.slice(i + 1) : s; };
  const bizImgUrl = useMemo(() => {
    if (!shop.bizImageKey || !shop.hubId || !shop.bizId) return null;
    return cdnUrl({ entity: "biz", fileType: "media", hubId: shop.hubId, bizId: shop.bizId, fileName: basenameOnly(shop.bizImageKey) });
  }, [shop.bizImageKey, shop.hubId, shop.bizId]);

  const categoryLines = getStoreCategoryLines({
    productSegmentId: shop.productSegment,
    serviceSegmentId: shop.serviceSegment,
  });
  const location = shop.city || "";

  const handleNameClick = (e) => {
    e.stopPropagation();
    if (!isDeleted) onManage?.(shop);
  };

  const handleManageClick = (e) => {
    e.stopPropagation();
    if (!isDeleted) onEdit?.(shop);
  };

  const handleMenuAction = (actionKey) => {
    switch (actionKey) {
      case "edit": onEdit?.(shop); break;
      case "preview": onPreview?.(shop); break;
      case "copyLink": handleCopyUrl(shop); break;
      case "qrCode": onQRCode?.(shop); break;
      case "delete": onDelete?.(shop); break;
      default: break;
    }
  };

  const handleCopyUrl = (shopData) => {
    const url = `https://${selectedHubDomain || shopData.bizId}.${FOOTER.CUSTOMER}/${shopData.bizId}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
  };

  return (
    <div
      id={getGuidedJourneyBusinessTarget(shop.bizId)}
      className={`${styles.card} ${isDeleted ? styles.cardDeleted : ""}`}
      aria-label={shop.name}
    >
      <BusinessCardMedia
        shop={shop}
        bizImgUrl={bizImgUrl}
        isDeleted={isDeleted}
        selectedHubDomain={selectedHubDomain}
        onPreview={onPreview}
        onQRCode={onQRCode}
      />

      <div className={styles.body}>
        <div
          className={styles.name}
          title={shop.name}
          onClick={handleNameClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") handleNameClick(e); }}
        >
          {shop.bizPrivate && <LockOutlined className={styles.nameLockIcon} />}
          {shop.name}
        </div>

        <div className={styles.meta}>
          {categoryLines.length > 0 && (
            <div className={styles.metaCategories}>
              {categoryLines.map((line) => {
                const CategoryIcon = getCategoryIcon(line.id);
                return (
                  <span key={line.id || line.label} className={styles.metaCategory}>
                    <CategoryIcon size={15} className={styles.metaIcon} aria-hidden="true" />
                    <span>{line.label}</span>
                  </span>
                );
              })}
            </div>
          )}
          {location ? (
            <span className={styles.metaLocation}>
              <MapPin size={15} className={styles.metaIcon} aria-hidden="true" />
              <span>{location}</span>
            </span>
          ) : null}
        </div>
      </div>

      <div className={styles.actions}>
        {isDeleted ? (
          <button className={styles.actionBtn} onClick={(e) => { e.stopPropagation(); onRestore?.(shop); }}>
            Restore
          </button>
        ) : (
          <>
            <button className={styles.actionBtn} onClick={handleManageClick}>
              Manage Business
            </button>
            <BusinessMenu onAction={handleMenuAction} />
          </>
        )}
      </div>

      {isDeleted && shop.timeToLive && (
        <div className={styles.deletedMessage}>
          Permanent delete: {formatTTL(shop.timeToLive)}
        </div>
      )}
    </div>
  );
});

function formatTTL(ttlSeconds) {
  if (!ttlSeconds || Number.isNaN(Number(ttlSeconds))) return "N/A";
  const date = new Date(Number(ttlSeconds) * 1000);
  if (Number.isNaN(date.getTime())) return "N/A";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${date.getFullYear()}-${months[date.getMonth()]}-${String(date.getDate()).padStart(2,"0")} ${String(date.getHours()).padStart(2,"0")}:${String(date.getMinutes()).padStart(2,"0")}`;
}

export default BusinessCard;
